import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select as RSelect } from 'chakra-react-select';
import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowClockwise,
  ArrowRight,
  CaretLeft,
  CaretRight,
  CheckCircle,
  ClockCounterClockwise,
  DownloadSimple,
  Eye,
  Receipt,
  Trash,
  WarningCircle,
} from 'phosphor-react';

import {
  IExtratoItem,
  useContaCilExtratoDetalhe,
  useContaCilExtratoLancamentos,
  useCorrigirExtrato,
  useExtratoCorrecoes,
  useValidarExtrato,
} from '../../../../../../hooks/useContaCilExtratos';
import {
  useContaCilContas,
  useCriarConta,
  usePlanoBancos,
} from '../../../../../../hooks/useContaCilContasBancarias';
import {
  IEmpresaOption,
  useContaCilAtribuirEmpresa,
} from '../../../../../../hooks/useContaCilImportar';
import { useContaCilRegrasBancos } from '../../../../../../hooks/useContaCilRegras';
import { extractApiErrorMessage } from '../../../../../../utils/ErrorUtils';
import { propsMenuSelect } from '../../../../../../utils/selectMenu';
import { EmpresaPicker } from '../../components/EmpresaPicker';
import { STATUS_UI, fmtData, fmtDataHora, fmtPeriodo, fmtValor } from '../format';
import { DivergenciaQuiz } from './DivergenciaQuiz';
import { ExtratoPdfModal } from './ExtratoPdfModal';
import { VeredictoChecklist } from './VeredictoExtrato';

const LANC_PAGE_SIZE = 50;
const ACAO_INDISPONIVEL =
  'Arquivo não guardado para este extrato (importado antes do armazenamento)';
// Conta/agência: só dígito, ponto e hífen (espelha o sanitizador do backend).
const soContaChars = (v: string) => v.replace(/[^\d.-]/g, '');

function renderBalanco(
  balancoOk?: boolean | null,
  divergencia?: number | null,
  verificado?: boolean | null,
) {
  if (balancoOk == null) return <Text>—</Text>;
  if (balancoOk) {
    // Fechou por derivação (SF=SI+net) sem saldo do banco pra confirmar =
    // não-verificado, revisar antes de conciliar.
    if (verificado === false) {
      return (
        <Text
          color="#B45309"
          title="Balanço calculado a partir dos lançamentos, sem saldo do banco pra confirmar — revise antes de conciliar"
        >
          não-verificado
        </Text>
      );
    }
    return <Text color="#059669">confere</Text>;
  }
  return <Text color="#DC2626">diverge {fmtValor(divergencia)}</Text>;
}

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box minW={0}>
      <Text
        fontSize="10px"
        fontWeight="600"
        color="#94A3B8"
        textTransform="uppercase"
        letterSpacing="0.06em"
        mb={0.5}
      >
        {label}
      </Text>
      <Box fontSize="sm" color="#0F172A" fontWeight="600">
        {children}
      </Box>
    </Box>
  );
}

interface Props {
  extrato: IExtratoItem | null;
  isOpen: boolean;
  onClose: () => void;
  onBaixar: (item: IExtratoItem) => void;
  onReprocessar: (item: IExtratoItem) => void;
  onExcluir: (item: IExtratoItem) => void;
  reprocessando: boolean;
}

export function ExtratoDetailModal({
  extrato,
  isOpen,
  onClose,
  onBaixar,
  onReprocessar,
  onExcluir,
  reprocessando,
}: Props) {
  const [page, setPage] = useState(1);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [histAberto, setHistAberto] = useState(false);
  const [empresaSel, setEmpresaSel] = useState<IEmpresaOption | null>(null);
  const [agenciaEdit, setAgenciaEdit] = useState('');
  const [bancoEdit, setBancoEdit] = useState('');
  const [contaEdit, setContaEdit] = useState('');
  const [pocCodigo, setPocCodigo] = useState(''); // POC-banco escolhida no plano
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const irParaConciliar = () => {
    if (!extrato?.empresa_id) return;
    onClose();
    navigate(
      `/modulesHome/accountingConciliation/conciliacao-contabil/conciliar?empresa=${extrato.empresa_id}`,
    );
  };

  const validar = useValidarExtrato();
  const corrigir = useCorrigirExtrato();
  const criarConta = useCriarConta();
  const atribuir = useContaCilAtribuirEmpresa();

  useEffect(() => {
    setPage(1);
    setHistAberto(false);
    setEmpresaSel(null);
    setAgenciaEdit(extrato?.agencia ?? '');
    setBancoEdit(extrato?.banco_nome ?? '');
    setContaEdit(extrato?.conta ?? '');
    setPocCodigo('');
  }, [extrato?.id]);

  const { data: detalhe } = useContaCilExtratoDetalhe(extrato?.id, isOpen);
  const { data: lancPage, isFetching } = useContaCilExtratoLancamentos(
    extrato?.id,
    isOpen,
    page,
    LANC_PAGE_SIZE,
  );
  const { data: correcoes = [] } = useExtratoCorrecoes(extrato?.id, isOpen && histAberto);
  // Contas bancarias da empresa (plano de contas) — pro picker do "Corrigir":
  // cada conta = banco + agencia + conta + POC. Escolher uma preenche os campos.
  const { data: contasBancarias = [] } = useContaCilContas(isOpen ? extrato?.empresa_id : null);
  // Contas-banco do plano de contas — pro picker quando a empresa ainda NÃO tem
  // conta cadastrada (a toolbar ficava vazia). A pessoa escolhe a POC do banco.
  const { data: planoBancos = [] } = usePlanoBancos(isOpen ? extrato?.empresa_id : null);
  // Catálogo FEBRABAN completo pro seletor de banco (substitui o texto livre).
  const { data: bancosCatalogo = [] } = useContaCilRegrasBancos();

  const lancamentos = lancPage?.items ?? [];
  const totalPages = lancPage?.pages ?? 1;
  const totalLanc = lancPage?.total ?? detalhe?.qtd_lancamentos ?? 0;
  const veredito = detalhe?.critica_saldo ?? extrato?.critica_saldo ?? null;
  const statusUi = extrato ? STATUS_UI[extrato.status] : null;
  const isAIdentificar =
    extrato?.status === 'a_identificar' || extrato?.status === 'aguardando_titular';
  // O que destrava o extrato é NÃO TER empresa, não o status. Documento retido
  // por tipo (`aguardando_validacao`) com CNPJ não detectado também chega aqui
  // sem empresa, e antes ficava sem saída: o seletor não aparecia e "Corrigir e
  // liberar" respondia 500. Atribuir nesse caso só vincula — quem materializa
  // continua sendo o "Corrigir e liberar", decisão humana e explícita.
  const semEmpresa = !!extrato && extrato.empresa_id == null;
  const vinculoApenas = semEmpresa && !isAIdentificar;
  const podeValidar = extrato?.pode_validar || extrato?.status === 'aguardando_validacao';
  // Divergência EXPLICADA pelo banco (check informativo do auditor): o saldo
  // não fecha, mas a diferença é provada como não-discriminada — o backend
  // PERMITE liberar (a retenção é por outro motivo, ex.: agência) e o quiz
  // resolve depois. Sem esta exceção o modal escondia "Validar e liberar" e
  // criava deadlock de UI (não libera → quiz read-only → nunca resolve).
  const divExplicada = (extrato?.critica_saldo?.checks ?? []).some(
    c => c.id === 'balanco_nao_discriminado',
  );
  // Divergência de balanço NÃO explicada: o saldo não fecha, então não há
  // liberação manual.
  const retidoPorBalanco = podeValidar && extrato?.balanco_ok === false && !divExplicada;
  let msgVazio = 'Nenhum lançamento vinculado a este extrato.';
  if (semEmpresa) {
    msgVazio = 'Extrato sem empresa — atribua a empresa para gerar os lançamentos.';
  } else if (retidoPorBalanco) {
    msgVazio = 'Nenhum lançamento gerado — o saldo do extrato não fecha.';
  } else if (podeValidar) {
    msgVazio = 'Extrato retido pelo auditor — valide e libere para gerar os lançamentos.';
  }
  const variacao =
    extrato?.saldo_inicial != null && extrato?.saldo_final != null
      ? extrato.saldo_final - extrato.saldo_inicial
      : null;

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['contacil', 'extratos'] });
    queryClient.invalidateQueries({ queryKey: ['contacil', 'extrato-detalhe'] });
    queryClient.invalidateQueries({ queryKey: ['contacil', 'extrato-lancamentos'] });
    queryClient.invalidateQueries({ queryKey: ['contacil', 'empresas-saude'] });
  };

  const onValidar = async () => {
    if (!extrato) return;
    try {
      const r = await validar.mutateAsync({ extratoId: extrato.id });
      toast({
        title: `Extrato liberado · ${r.lancamentos_afetados} lançamento(s)`,
        status: 'success',
      });
      invalidar();
      onClose();
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, 'Falha ao liberar.'), status: 'error' });
    }
  };

  const onAtribuir = async () => {
    if (!extrato || !empresaSel) return;
    try {
      await atribuir.mutateAsync({ importId: extrato.id, empresaId: empresaSel.value });
      toast({
        title: vinculoApenas
          ? 'Empresa vinculada — agora use "Corrigir e liberar".'
          : 'Empresa atribuída — extrato reprocessado.',
        status: 'success',
      });
      invalidar();
      // Vínculo apenas: o extrato SEGUE aberto na tela, porque o passo seguinte
      // (liberar) é no mesmo modal. Fechar aqui obrigaria a reabrir o card.
      if (!vinculoApenas) onClose();
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, 'Falha ao atribuir empresa.'), status: 'error' });
    }
  };

  // Preenche o que faltou (ex.: agência) e libera com a auditoria limpa:
  // corrige + re-audita no backend, depois materializa com os dados corrigidos.
  const onCorrigirELiberar = async () => {
    if (!extrato) return;
    // O `corrigir` exige ao menos UMA alteração (senão o backend devolve 400
    // "Nenhuma correção informada"). Quando o extrato já está completo e só
    // precisa liberar (retido por confiança/ETL, não por falta de dado), pula
    // o corrigir e vai direto pro validar — o botão funciona dos dois jeitos.
    const mudou =
      (agenciaEdit.trim() || '') !== (extrato.agencia ?? '') ||
      (bancoEdit.trim() || '') !== (extrato.banco_nome ?? '') ||
      (contaEdit.trim() || '') !== (extrato.conta ?? '');
    try {
      // Escolheu uma POC-banco do plano + tem conta: cadastra a conta
      // (vincula a POC) antes de liberar. Assim a materialização usa a POC
      // certa e a conta passa a aparecer no picker nas próximas vezes.
      if (pocCodigo && contaEdit.trim() && extrato.empresa_id && contasBancarias.length === 0) {
        await criarConta.mutateAsync({
          empresa_id: extrato.empresa_id,
          banco_nome: bancoEdit.trim(),
          agencia: agenciaEdit.trim() || undefined,
          conta: contaEdit.trim(),
          poc_codigo: pocCodigo,
        });
      }
      if (mudou) {
        await corrigir.mutateAsync({
          extratoId: extrato.id,
          agencia: agenciaEdit.trim() || undefined,
          bancoNome: bancoEdit.trim() || undefined,
          conta: contaEdit.trim() || undefined,
        });
      }
      const r = await validar.mutateAsync({ extratoId: extrato.id });
      toast({
        title: `Extrato ${mudou ? 'corrigido e ' : ''}liberado · ${r.lancamentos_afetados} lançamento(s)`,
        status: 'success',
      });
      invalidar();
      onClose();
    } catch (err) {
      // corrigir pode ter sucedido e validar falhado: revalida pra UI refletir
      // o estado real (corrigido mas ainda retido), sem fechar o modal.
      invalidar();
      toast({ title: extractApiErrorMessage(err, 'Falha ao corrigir/liberar.'), status: 'error' });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      // Mesma trava do "Ver extrato", agora com o "Ver PDF" por cima: o Chakra
      // só liga o RemoveScroll do modal da BASE da pilha, e a trava dele bloqueia
      // a roda do mouse sobre o modal de cima. Ver a nota no LancamentoDetailModal.
      blockScrollOnMount={!pdfOpen}
      onClose={onClose}
      size={{ base: 'full', md: '3xl' }}
      isCentered
      scrollBehavior="inside"
      motionPreset="slideInBottom"
    >
      <ModalOverlay
        bg="rgba(15, 23, 42, 0.45)"
        backdropFilter="blur(8px)"
        sx={{ WebkitBackdropFilter: 'blur(8px)' }}
      />
      <ModalContent
        bg="rgba(255, 255, 255, 0.82)"
        backdropFilter="blur(40px) saturate(200%)"
        sx={{ WebkitBackdropFilter: 'blur(40px) saturate(200%)' }}
        borderWidth="1px"
        borderColor="rgba(255, 255, 255, 0.6)"
        borderRadius={{ base: 'none', md: '3xl' }}
        boxShadow="0 1px 0 0 rgba(255,255,255,0.9) inset, 0 24px 70px -18px rgba(27, 136, 245, 0.30), 0 8px 24px -8px rgba(15, 23, 42, 0.12)"
        maxH={{ base: '100dvh', md: '88vh' }}
        overflow="hidden"
      >
        <ModalHeader pt={5} pb={2}>
          <Flex align="center" gap={3}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="44px"
              h="44px"
              borderRadius="xl"
              bg="linear-gradient(135deg, #E0E7FF 0%, #DDD6FE 100%)"
              color="#6366F1"
              flexShrink={0}
            >
              <Receipt size={24} weight="duotone" />
            </Box>
            <Box minW={0} flex={1}>
              <Flex align="center" gap={2} wrap="wrap">
                <Heading
                  fontSize="lg"
                  fontWeight="700"
                  color="#0F172A"
                  lineHeight="1.2"
                  isTruncated
                  maxW="300px"
                >
                  {extrato?.nome_arquivo ?? 'Extrato'}
                </Heading>
                <Badge colorScheme="gray" borderRadius="full" px={2}>
                  {extrato?.formato}
                </Badge>
                {statusUi && (
                  <Badge colorScheme={statusUi.cor} borderRadius="full" px={2}>
                    {statusUi.label}
                  </Badge>
                )}
              </Flex>
              <Text fontSize="xs" color="#64748B" isTruncated>
                {extrato?.empresa_nome ?? extrato?.titular_detectado ?? 'Detalhes da importação'}
              </Text>
            </Box>
          </Flex>
        </ModalHeader>
        <ModalCloseButton top={4} color="#64748B" />

        <ModalBody pb={4} pt={2}>
          {extrato && (
            <>
              {/* Resumo */}
              <SimpleGrid columns={{ base: 2, md: 3 }} spacingX={4} spacingY={4} mb={5}>
                <Campo label="Empresa">{extrato.empresa_nome ?? '—'}</Campo>
                <Campo label="Banco / Conta">
                  {extrato.banco_nome ?? '—'}
                  {extrato.agencia ? ` · ag ${extrato.agencia}` : ''}
                  {extrato.conta ? ` · ${extrato.conta}` : ''}
                </Campo>
                <Campo label="Período">
                  {fmtPeriodo(extrato.periodo_ini, extrato.periodo_fim)}
                </Campo>
                <Campo label="Saldo inicial">{fmtValor(extrato.saldo_inicial)}</Campo>
                <Campo label="Saldo final">{fmtValor(extrato.saldo_final)}</Campo>
                <Campo label="Variação">
                  {variacao == null ? (
                    '—'
                  ) : (
                    <Text color={variacao >= 0 ? '#059669' : '#DC2626'}>
                      {variacao >= 0 ? '+' : '−'} {fmtValor(Math.abs(variacao))}
                    </Text>
                  )}
                </Campo>
                <Campo label="Balanço">
                  {renderBalanco(
                    extrato.balanco_ok,
                    extrato.divergencia_balanco,
                    extrato.balanco_verificado,
                  )}
                </Campo>
                <Campo label="Transações">
                  {extrato.qtd_transacoes ?? '—'}
                  {(extrato.qtd_novas != null || extrato.qtd_duplicadas != null) && (
                    <Text as="span" fontSize="11px" color="#64748B" fontWeight="500" ml={1}>
                      ({extrato.qtd_novas ?? 0} novas · {extrato.qtd_duplicadas ?? 0} dup.)
                    </Text>
                  )}
                </Campo>
                <Campo label="Confiança">
                  {extrato.confianca_global != null
                    ? `${Math.round(extrato.confianca_global * 100)}%`
                    : '—'}
                </Campo>
              </SimpleGrid>

              {/* Auditoria do extrato (veredito + checklist) */}
              <Box mb={4}>
                <VeredictoChecklist veredito={veredito} />
              </Box>

              {/* Divergência não discriminada pelo banco (quiz de resolução).
                  Extrato não-concluído (retido/processando): somente leitura —
                  rotular criaria lançamento avulso antes da materialização. */}
              {extrato && (
                <DivergenciaQuiz
                  extratoId={extrato.id}
                  janelas={detalhe?.divergencias}
                  somenteLeitura={extrato.status !== 'concluido'}
                />
              )}

              {/* Sem botão próprio: reprocessar repete a mesma leitura do mesmo
                  arquivo. O do rodapé basta. */}
              {retidoPorBalanco && (
                <Box
                  borderWidth="1px"
                  borderColor="rgba(239, 68, 68, 0.35)"
                  bg="rgba(239, 68, 68, 0.05)"
                  borderRadius="xl"
                  p={4}
                  mb={4}
                >
                  <Flex align="center" gap={2} mb={1}>
                    <Box color="#DC2626">
                      <WarningCircle size={16} weight="fill" />
                    </Box>
                    <Text fontSize="13px" fontWeight="700" color="#B91C1C">
                      Retido por divergência de balanço
                    </Text>
                  </Flex>
                  <Text fontSize="12px" color="#64748B" mb={3}>
                    Os lançamentos lidos não somam o saldo final do documento
                    {/* abs: o backend passa a divergência crua; "faltam -350,00" não faz sentido. */}
                    {extrato?.divergencia_balanco != null
                      ? `, e faltam ${fmtValor(Math.abs(extrato.divergencia_balanco))}`
                      : ''}
                    . Em geral é lançamento que não foi lido. Liberar assim criaria uma conciliação
                    que não bate com o extrato, então esta etapa fica travada.
                  </Text>
                  <Text fontSize="12px" color="#64748B">
                    {extrato?.pode_reprocessar
                      ? 'Reprocessar está no rodapé, e só muda o resultado se a leitura do documento tiver sido corrigida.'
                      : 'O arquivo não está mais guardado — reenvie o documento para tentar de novo.'}
                  </Text>
                </Box>
              )}

              {/* Corrigir + liberar (retido por metadados: banco/agência/conta) */}
              {podeValidar && !retidoPorBalanco && (
                <Box
                  borderWidth="1px"
                  borderColor="rgba(245, 158, 11, 0.35)"
                  bg="rgba(245, 158, 11, 0.05)"
                  borderRadius="xl"
                  p={4}
                  mb={4}
                >
                  <Text fontSize="13px" fontWeight="700" color="#B45309" mb={1}>
                    Completar e liberar
                  </Text>
                  <Text fontSize="12px" color="#64748B" mb={3}>
                    A agência é obrigatória para liberar. Escolha a conta bancária do plano de
                    contas (preenche banco · agência · conta) ou ajuste manualmente.
                  </Text>
                  {contasBancarias.length > 0 && (
                    <Box mb={3}>
                      <Text fontSize="10px" fontWeight="600" color="#64748B" mb={1}>
                        Conta bancária (plano de contas)
                      </Text>
                      <Select
                        size="sm"
                        borderRadius="lg"
                        bg="white"
                        placeholder="Escolher a conta do plano…"
                        onChange={e => {
                          // Placeholder do Chakra Select e <option value="">;
                          // Number('') === 0 selecionaria a 1a conta. Guardar.
                          if (e.target.value === '') return;
                          const c = contasBancarias[Number(e.target.value)];
                          if (!c) return;
                          setBancoEdit(c.banco_nome ?? '');
                          setAgenciaEdit(c.agencia ?? '');
                          setContaEdit(c.conta ?? '');
                        }}
                      >
                        {contasBancarias.map((c, i) => (
                          <option key={`${c.id ?? 'novo'}-${c.conta}`} value={i}>
                            {c.banco_nome}
                            {c.agencia ? ` · ag ${c.agencia}` : ''}
                            {` · ${c.conta}`}
                            {c.poc_nome ? ` · ${c.poc_nome}` : ''}
                          </option>
                        ))}
                      </Select>
                    </Box>
                  )}
                  {contasBancarias.length === 0 && planoBancos.length > 0 && (
                    <Box mb={3}>
                      <Text fontSize="10px" fontWeight="600" color="#64748B" mb={1}>
                        Conta-banco do plano de contas
                      </Text>
                      <Select
                        size="sm"
                        borderRadius="lg"
                        bg="white"
                        value={pocCodigo}
                        placeholder="Escolher a conta do banco no plano…"
                        onChange={e => setPocCodigo(e.target.value)}
                      >
                        {planoBancos.map(pb => (
                          <option key={pb.poc_codigo} value={pb.poc_codigo}>
                            {pb.poc_codigo} · {pb.poc_nome}
                          </option>
                        ))}
                      </Select>
                      <Text fontSize="10px" color="#94A3B8" mt={1}>
                        Empresa sem conta cadastrada — escolha a POC do banco e preencha a
                        agência/conta abaixo; a conta é cadastrada ao liberar.
                      </Text>
                    </Box>
                  )}
                  <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={2} mb={3}>
                    <Box>
                      <Text fontSize="10px" fontWeight="600" color="#64748B" mb={1}>
                        Agência
                      </Text>
                      <Input
                        size="sm"
                        value={agenciaEdit}
                        onChange={e => setAgenciaEdit(soContaChars(e.target.value))}
                        placeholder="ex.: 1234"
                        inputMode="numeric"
                        borderRadius="lg"
                        bg="white"
                      />
                    </Box>
                    <Box>
                      <Text fontSize="10px" fontWeight="600" color="#64748B" mb={1}>
                        Banco
                      </Text>
                      <RSelect
                        size="sm"
                        isSearchable
                        isClearable
                        placeholder="Selecionar banco…"
                        options={bancosCatalogo.map(b => ({
                          value: b.slug,
                          label: b.nome,
                        }))}
                        value={
                          bancoEdit
                            ? {
                                value: bancoEdit,
                                label:
                                  bancosCatalogo.find(b => b.slug === bancoEdit)?.nome ??
                                  `${bancoEdit} (atual)`,
                              }
                            : null
                        }
                        onChange={opt =>
                          setBancoEdit((opt as { value: string } | null)?.value ?? '')
                        }
                        menuPortalTarget={
                          typeof document !== 'undefined' ? document.body : undefined
                        }
                        styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                        {...propsMenuSelect}
                        chakraStyles={{
                          control: provided => ({
                            ...provided,
                            borderRadius: 'lg',
                            background: 'white',
                          }),
                        }}
                      />
                    </Box>
                    <Box>
                      <Text fontSize="10px" fontWeight="600" color="#64748B" mb={1}>
                        Conta
                      </Text>
                      <Input
                        size="sm"
                        value={contaEdit}
                        onChange={e => setContaEdit(soContaChars(e.target.value))}
                        placeholder="ex.: 12345-6"
                        inputMode="numeric"
                        borderRadius="lg"
                        bg="white"
                      />
                    </Box>
                  </SimpleGrid>
                  <Button
                    size="sm"
                    borderRadius="lg"
                    bg="#F59E0B"
                    color="white"
                    _hover={{ bg: '#D97706' }}
                    leftIcon={<CheckCircle size={16} weight="bold" />}
                    isLoading={corrigir.isPending || validar.isPending}
                    isDisabled={!agenciaEdit.trim() || corrigir.isPending || validar.isPending}
                    onClick={onCorrigirELiberar}
                  >
                    Corrigir e liberar
                  </Button>
                  {!agenciaEdit.trim() && (
                    <Text fontSize="11px" color="#B45309" mt={2}>
                      A agência é obrigatória para liberar — escolha a conta ou preencha acima.
                    </Text>
                  )}
                </Box>
              )}

              {/* Atribuir empresa (qualquer extrato sem vínculo) */}
              {semEmpresa && (
                <Box
                  borderWidth="1px"
                  borderColor="rgba(245, 158, 11, 0.35)"
                  bg="rgba(245, 158, 11, 0.06)"
                  borderRadius="xl"
                  p={4}
                  mb={4}
                >
                  <Text fontSize="13px" fontWeight="700" color="#B45309" mb={1}>
                    Empresa não identificada
                  </Text>
                  <Text fontSize="12px" color="#64748B" mb={3}>
                    CNPJ detectado: {extrato.cnpj_detectado || '—'}
                    {extrato.titular_detectado ? ` · ${extrato.titular_detectado}` : ''}.{' '}
                    {vinculoApenas
                      ? 'Escolha a empresa para vincular o documento. Os lançamentos só são gerados depois, em "Corrigir e liberar".'
                      : 'Escolha a empresa para gerar os lançamentos.'}
                  </Text>
                  <Flex gap={2} direction={{ base: 'column', sm: 'row' }} align={{ sm: 'end' }}>
                    <Box flex={1}>
                      <EmpresaPicker
                        label=""
                        value={empresaSel}
                        onChange={setEmpresaSel}
                        placeholderOverride="Buscar empresa..."
                      />
                    </Box>
                    <Button
                      size="sm"
                      borderRadius="lg"
                      bg="#F59E0B"
                      color="white"
                      _hover={{ bg: '#D97706' }}
                      isDisabled={!empresaSel}
                      isLoading={atribuir.isPending}
                      onClick={onAtribuir}
                    >
                      Atribuir
                    </Button>
                  </Flex>
                </Box>
              )}

              {/* Erro de processamento. Inclui `quarentena`: aquele registro
                  TEM a mensagem gravada ("Processamento falhou completamente"),
                  e sem esta condição o documento abria sem dizer por que está
                  parado — justamente o que o card novo passou a tornar
                  alcançável. */}
              {(extrato.status === 'erro' || extrato.status === 'quarentena') &&
                detalhe?.erro_processamento && (
                  <Flex
                    align="flex-start"
                    gap={2}
                    bg="rgba(239, 68, 68, 0.1)"
                    color="#DC2626"
                    borderRadius="xl"
                    p={3}
                    mb={4}
                    fontSize="13px"
                  >
                    <Box mt="2px" flexShrink={0}>
                      <WarningCircle size={16} weight="fill" />
                    </Box>
                    <Text>{detalhe.erro_processamento}</Text>
                  </Flex>
                )}

              {/* Lançamentos */}
              <Flex align="center" gap={2} mb={2}>
                <Text fontSize="sm" fontWeight="700" color="#0F172A">
                  Lançamentos
                </Text>
                <Badge colorScheme="gray" borderRadius="full">
                  {totalLanc}
                </Badge>
                {isFetching && <Spinner size="xs" color="#1B88F5" thickness="2px" />}
              </Flex>

              {lancamentos.length === 0 ? (
                <Box
                  py={8}
                  borderWidth="1px"
                  borderColor="rgba(15, 23, 42, 0.06)"
                  borderRadius="xl"
                  bg="rgba(255, 255, 255, 0.5)"
                >
                  <Text fontSize="sm" color="#94A3B8" textAlign="center" px={4}>
                    {msgVazio}
                  </Text>
                </Box>
              ) : (
                <Box
                  borderWidth="1px"
                  borderColor="rgba(15, 23, 42, 0.06)"
                  borderRadius="xl"
                  overflow="hidden"
                >
                  <TableContainer>
                    <Table size="sm" variant="simple">
                      <Thead bg="rgba(15, 23, 42, 0.02)">
                        <Tr>
                          <Th>Data</Th>
                          <Th>Histórico</Th>
                          <Th isNumeric>Valor</Th>
                          <Th>Categoria</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {lancamentos.map(l => (
                          <Tr key={l.id} _hover={{ bg: 'rgba(27, 136, 245, 0.04)' }}>
                            <Td whiteSpace="nowrap" fontSize="xs" color="#475569">
                              {fmtData(l.data)}
                            </Td>
                            <Td fontSize="xs" color="#0F172A" maxW="260px">
                              <Text isTruncated title={l.historico}>
                                {l.historico}
                              </Text>
                              {l.contraparte && (
                                <Text fontSize="10px" color="#94A3B8" isTruncated>
                                  {l.contraparte}
                                </Text>
                              )}
                            </Td>
                            <Td
                              isNumeric
                              whiteSpace="nowrap"
                              fontSize="xs"
                              fontWeight="600"
                              color={l.dc === 'C' ? '#10B981' : '#EF4444'}
                            >
                              {l.dc === 'C' ? '+' : '−'} {fmtValor(l.valor)}
                            </Td>
                            <Td fontSize="11px" color="#64748B" maxW="140px">
                              <Text isTruncated title={l.categoria ?? ''}>
                                {l.categoria ?? '—'}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  {totalPages > 1 && (
                    <Flex
                      align="center"
                      justify="center"
                      gap={3}
                      py={2}
                      borderTopWidth="1px"
                      borderColor="rgba(15, 23, 42, 0.06)"
                    >
                      <IconButton
                        aria-label="Página anterior"
                        icon={<CaretLeft size={14} weight="bold" />}
                        size="xs"
                        variant="ghost"
                        borderRadius="full"
                        isDisabled={page <= 1}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                      />
                      <Text fontSize="xs" color="#64748B" fontWeight="600">
                        {page} / {totalPages}
                      </Text>
                      <IconButton
                        aria-label="Próxima página"
                        icon={<CaretRight size={14} weight="bold" />}
                        size="xs"
                        variant="ghost"
                        borderRadius="full"
                        isDisabled={page >= totalPages}
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      />
                    </Flex>
                  )}
                </Box>
              )}

              {/* Histórico de correções */}
              <Flex
                as="button"
                align="center"
                gap={2}
                mt={4}
                onClick={() => setHistAberto(a => !a)}
                aria-expanded={histAberto}
              >
                <Box color="#94A3B8">
                  <ClockCounterClockwise size={15} weight="bold" />
                </Box>
                <Text fontSize="13px" fontWeight="600" color="#64748B">
                  Histórico de correções
                </Text>
                {correcoes.length > 0 && (
                  <Badge colorScheme="gray" borderRadius="full" fontSize="9px">
                    {correcoes.length}
                  </Badge>
                )}
              </Flex>
              <Collapse in={histAberto} animateOpacity>
                <Box mt={2}>
                  {correcoes.length === 0 ? (
                    <Text fontSize="12px" color="#94A3B8">
                      Nenhuma correção registrada.
                    </Text>
                  ) : (
                    <Flex direction="column" gap={2}>
                      {correcoes.map(c => (
                        <Flex
                          key={c.id}
                          align="center"
                          gap={2}
                          px={3}
                          py={2}
                          borderWidth="1px"
                          borderColor="rgba(15,23,42,0.06)"
                          borderRadius="lg"
                          bg="rgba(255,255,255,0.5)"
                        >
                          <Badge colorScheme="purple" borderRadius="full" fontSize="9px">
                            {c.tipo}
                          </Badge>
                          <Text fontSize="12px" color="#0F172A" flex={1} minW={0} isTruncated>
                            {c.campo ? `${c.campo}: ` : ''}
                            {c.valor_antes ? `${c.valor_antes} → ` : ''}
                            {c.valor_depois ?? c.observacao ?? '—'}
                          </Text>
                          <Text fontSize="10px" color="#94A3B8" flexShrink={0}>
                            {c.usuario_email ?? ''} · {fmtDataHora(c.dt_criacao)}
                          </Text>
                        </Flex>
                      ))}
                    </Flex>
                  )}
                </Box>
              </Collapse>
            </>
          )}
        </ModalBody>

        {extrato && (
          <ModalFooter
            gap={2}
            borderTopWidth="1px"
            borderColor="rgba(15, 23, 42, 0.06)"
            flexWrap="wrap"
          >
            <Tooltip
              label={
                extrato.pode_baixar
                  ? `Ver ${(extrato.formato || 'arquivo').toUpperCase()} do extrato`
                  : ACAO_INDISPONIVEL
              }
              hasArrow
              shouldWrapChildren
            >
              <Button
                size="sm"
                leftIcon={<Eye size={16} weight="bold" />}
                variant="outline"
                borderRadius="lg"
                isDisabled={!extrato.pode_baixar}
                onClick={() => setPdfOpen(true)}
              >
                {`Ver ${(extrato.formato || 'arquivo').toUpperCase()}`}
              </Button>
            </Tooltip>
            <Tooltip
              label={extrato.pode_baixar ? 'Baixar arquivo original' : ACAO_INDISPONIVEL}
              hasArrow
              shouldWrapChildren
            >
              <Button
                size="sm"
                leftIcon={<DownloadSimple size={16} weight="bold" />}
                variant="outline"
                borderRadius="lg"
                isDisabled={!extrato.pode_baixar}
                onClick={() => onBaixar(extrato)}
              >
                Baixar
              </Button>
            </Tooltip>
            <Tooltip
              label={extrato.pode_reprocessar ? 'Ler o documento de novo' : ACAO_INDISPONIVEL}
              hasArrow
              shouldWrapChildren
            >
              <Button
                size="sm"
                leftIcon={<ArrowClockwise size={16} weight="bold" />}
                variant="outline"
                borderRadius="lg"
                isLoading={reprocessando}
                isDisabled={!extrato.pode_reprocessar}
                onClick={() => onReprocessar(extrato)}
              >
                Reprocessar
              </Button>
            </Tooltip>
            <Box flex={1} />
            <Tooltip
              label={
                extrato.empresa_id
                  ? 'Abrir o Conciliar já filtrando esta empresa'
                  : 'Atribua uma empresa ao extrato para conciliar'
              }
              hasArrow
              shouldWrapChildren
            >
              <Button
                size="sm"
                rightIcon={<ArrowRight size={16} weight="bold" />}
                variant="outline"
                borderRadius="lg"
                isDisabled={!extrato.empresa_id}
                onClick={irParaConciliar}
              >
                Ir pro Conciliar
              </Button>
            </Tooltip>
            {/* "Validar e liberar" (libera direto) so' quando a agencia ja existe.
                Sem agencia, o unico caminho e' "Corrigir e liberar" (preencher). */}
            {podeValidar && !retidoPorBalanco && !!(extrato?.agencia || '').trim() && (
              <Button
                size="sm"
                leftIcon={<CheckCircle size={16} weight="bold" />}
                bg="#10B981"
                color="white"
                _hover={{ bg: '#059669' }}
                borderRadius="lg"
                isLoading={validar.isPending}
                onClick={onValidar}
              >
                Validar e liberar
              </Button>
            )}
            <Button
              size="sm"
              leftIcon={<Trash size={16} weight="bold" />}
              colorScheme="red"
              variant="ghost"
              borderRadius="lg"
              onClick={() => onExcluir(extrato)}
            >
              Excluir
            </Button>
          </ModalFooter>
        )}
      </ModalContent>

      <ExtratoPdfModal
        extratoId={pdfOpen ? (extrato?.id ?? null) : null}
        nomeArquivo={extrato?.nome_arquivo}
        formato={extrato?.formato}
        isOpen={pdfOpen}
        onClose={() => setPdfOpen(false)}
      />
    </Modal>
  );
}
